import { memo } from "react";

import { formatDistanceToNow } from "date-fns";
import { MessageSquare, Brain, Clock, Archive } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface BranchMetadataProps {
  newMessages: number;
  memoriesCreated: number;
  lastActivityAt: string;
  archived?: boolean;
  compact?: boolean;
}

const BranchMetadataComponent = ({
  newMessages,
  memoriesCreated,
  lastActivityAt,
  archived,
  compact = false
}: BranchMetadataProps) => {
  if (compact) {
    return (
      <TooltipProvider>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {archived && (
            <Tooltip>
              <TooltipTrigger>
                <Badge variant="outline" className="flex items-center gap-1">
                  <Archive className="h-3 w-3" />
                  Archived
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                This branch has been archived
              </TooltipContent>
            </Tooltip>
          )}
          
          <Tooltip>
            <TooltipTrigger>
              <div className="flex items-center gap-1">
                <MessageSquare className="h-3 w-3" />
                <span>{newMessages}</span>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              {newMessages} new {newMessages === 1 ? 'message' : 'messages'} in this branch
            </TooltipContent>
          </Tooltip>

          {memoriesCreated > 0 && (
            <Tooltip>
              <TooltipTrigger>
                <div className="flex items-center gap-1">
                  <Brain className="h-3 w-3" />
                  <span>{memoriesCreated}</span>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                {memoriesCreated} {memoriesCreated === 1 ? 'memory' : 'memories'} created
              </TooltipContent>
            </Tooltip>
          )}

          <Tooltip>
            <TooltipTrigger>
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                <span>{formatDistanceToNow(new Date(lastActivityAt), { addSuffix: true })}</span>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              Last activity: {new Date(lastActivityAt).toLocaleString()}
            </TooltipContent>
          </Tooltip>
        </div>
      </TooltipProvider>
    );
  }

  return (
    <div className="flex flex-wrap gap-3 text-sm">
      {archived && (
        <Badge variant="outline" className="flex items-center gap-1.5">
          <Archive className="h-3.5 w-3.5" />
          Archived
        </Badge>
      )}
      
      <div className="flex items-center gap-1.5 text-muted-foreground">
        <MessageSquare className="h-4 w-4" />
        <span>{newMessages} new {newMessages === 1 ? 'message' : 'messages'}</span>
      </div>

      {memoriesCreated > 0 && (
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <Brain className="h-4 w-4" />
          <span>{memoriesCreated} {memoriesCreated === 1 ? 'memory' : 'memories'}</span>
        </div>
      )}

      <div className="flex items-center gap-1.5 text-muted-foreground">
        <Clock className="h-4 w-4" />
        <span>Active {formatDistanceToNow(new Date(lastActivityAt), { addSuffix: true })}</span>
      </div>
    </div>
  );
};

export const BranchMetadata = memo(BranchMetadataComponent);
